import * as React from 'react';
import styled from '@independent-software/typeui/styles/Theme'

import { Input } from '@independent-software/typeui/controls/Input';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Form } from '@independent-software/typeui/controls/Form';
import { DmsInput } from '@independent-software/typeui/modules/DmsInput';

import { AuthContext } from '../../AuthContext';
import { Station, Stationcategory, StationcategoryFactory } from '../../resource/';
import { Query } from '../../services/';
import { Section } from '../../modules';
import { CategoryTree } from '../../controls/CategoryTree';
import { PickLocationMap } from '../../maps/PickLocationMap/PickLocationMap';

interface IProps {
  /** Initial form data. */
  data: Station;
  /** Called whenever form changes. */
  onChange: (data: Station, forceupdate: boolean) => void;
  /** Called whenever a field validates. Returns validation state for whole form. */
  onValidate: (valid: boolean) => void;
  /** 
   * Mark form as dirty. If dirty, it will show error messages for all fields,
   * even pristine fields.
   */  
  dirty?: boolean;
}

const StationForm = (props: IProps) => {
  const auth = React.useContext(AuthContext);
  const [stationcategories, setStationcategories] = React.useState([] as Stationcategory[]);

  React.useEffect(() => {
    // Retrieve a list of stationcategories:
    let query = new Query('name', 'asc');
    StationcategoryFactory.getSome(auth, 0, 999, query)
      .then((res) => setStationcategories(res.items));
  }, []);

  const renderName = () =>
    <Form.Field 
      name="name" label="Name"
      value={props.data.name}
      control={(<Input type="text" maxLength={20} placeholder="Name" fluid/>)}
      required={{message: "A station name is required."}}
      maxLength={{length: 20, message: "Station name may not have more than 20 characters."}}
      hint={(<React.Fragment>Please enter a station name.</React.Fragment>)}/>

  const renderDescription = () =>
    <Form.Field 
      name="description" label="Description"
      value={props.data.description}
      control={(<Input type="text" placeholder="Description" fluid/>)}
      hint={(<React.Fragment>Please enter a station description.</React.Fragment>)}/>

  const renderLatitude = () => 
    <Form.Field 
      forceupdate
      name="latitude" label="Latitude"
      value={props.data.latitude}
      control={(<DmsInput isLatitude={true}/>)}
      isFloat={{message: "Station latitude must be a numeric value."}}
      between={{min: -90, max: 90, message: "Station latitude must be between -90 and 90."}}
      hint={(<React.Fragment>Please enter latitude either as degrees, minutes, seconds or as a decimal.</React.Fragment>)}/>            

  const renderLongitude = () =>
    <Form.Field 
      forceupdate
      name="longitude" label="Longitude"
      value={props.data.longitude}
      control={(<DmsInput isLatitude={false}/>)}
      isFloat={{message: "Station longitude must be a numeric value."}}
      between={{min: -180, max: 180, message: "Station longitude must be between -180 and 180."}}
      hint={(<React.Fragment>Please enter longitude either as degrees, minutes, seconds or as a decimal.</React.Fragment>)}/>            

  const renderDepth = () => 
    <Form.Field 
      name="depth" label="Depth"
      value={props.data.depth}
      control={(<Input type="text" placeholder="Depth" fluid/>)}
      isFloat={{message: "Station depth must be a numeric value."}}
      hint={(<React.Fragment>Please enter a station depth in meters below tidal datum (hydrographic zero).</React.Fragment>)}/>              

  const handleCategoryClick = (cat: Stationcategory) => {
    let idx = props.data.stationcategories.findIndex(mycat => mycat.id == cat.id);
    if(idx == -1) {
      props.data.stationcategories.push(cat);
    } else {
      props.data.stationcategories.splice(idx, 1);
    }
    props.onChange(props.data, true);
  }

  return (
    <Form
      data={props.data} dirty={props.dirty} onChange={props.onChange} onValidate={props.onValidate}>
      <Section padded>
        <Flex stackable>
          <Flex.Row>
            <Flex.Column width={2}>
              <Flex.Columns count={2}>
                {renderName()}
                {renderDescription()}
                {renderLatitude()}
                {renderLongitude()}
                {renderDepth()}
              </Flex.Columns>
            </Flex.Column>
            <Flex.Column width={1}>
              <Form.Uncontrolled label="Categories">
                <TreeContainer>
                  <CategoryTree 
                    items={stationcategories} 
                    active={props.data.stationcategories.map(sc => sc.id)}
                    onClick={handleCategoryClick}/>
                </TreeContainer>
              </Form.Uncontrolled>
            </Flex.Column>
          </Flex.Row>
        </Flex>
      </Section>
      <PickLocationMap 
        zoom={11} 
        lat={props.data.latitude}
        lng={props.data.longitude}
        onSelect={(lat: number, lng: number) => {
          props.data.latitude = lat;
          props.data.longitude = lng;
          props.onChange(props.data, true);
        }}
      />
    </Form>
  )
}

const TreeContainer = styled.div`
  width: 100%;
  height: 280px;
  overflow-y: scroll;
  border: solid 1px #E0E1E2;
`

export { StationForm }
